import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Plus, X, Pencil, Trash2, KeyRound, Building2, ShieldCheck, Copy } from "lucide-react";

const STATO = { attivo: "bg-emerald-100 text-emerald-800", sospeso: "bg-amber-100 text-amber-800", disattivato: "bg-slate-200 text-slate-600" };
const EMPTY = { denominazione: "", referente: "", codice_fiscale: "", partita_iva: "", indirizzo: "", telefono: "", email: "", pec: "", login_email: "", password: "", stato: "attivo", permessi: { view: true, download: true, upload: false }, note: "" };
const inp = "mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg";

export default function StudiosPage() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const [creds, setCreds] = useState(null);

  const load = async () => { try { const { data } = await api.get("/studios"); setItems(data); } catch { toast.error("Solo admin"); } };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Eliminare lo Studio "${s.denominazione}"? Le aziende assegnate torneranno non assegnate.`)) return;
    await api.delete(`/studios/${s.id}`); toast.success("Studio eliminato"); load();
  };
  const resetCreds = async (s) => {
    if (!window.confirm(`Generare nuove credenziali per ${s.login_email}? Verranno inviate via email.`)) return;
    const { data } = await api.post(`/studios/${s.id}/reset-credentials`);
    setCreds({ ...data, denominazione: s.denominazione });
  };
  const setStato = async (s, stato) => {
    await api.put(`/studios/${s.id}`, { ...s, stato, password: "" }); toast.success(`Stato: ${stato}`); load();
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Super Admin</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Briefcase size={28} /> Studi di Consulenza</h1>
          <p className="text-slate-500 mt-1">Account isolati con area riservata: vedono solo le aziende assegnate, con permessi configurabili.</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} data-testid="add-studio"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-medium"><Plus size={18} /> Crea Studio di Consulenza</button>
      </header>

      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left px-4 py-3 font-medium">Studio</th><th className="text-left px-4 py-3 font-medium">Referente</th>
            <th className="text-left px-4 py-3 font-medium">Login</th><th className="text-left px-4 py-3 font-medium">Permessi</th>
            <th className="text-left px-4 py-3 font-medium">Aziende</th><th className="text-left px-4 py-3 font-medium">Stato</th>
            <th className="text-right px-4 py-3 font-medium">Azioni</th></tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`studio-row-${s.id}`}>
                <td className="px-4 py-3"><div className="font-medium">{s.denominazione}</div><div className="text-xs text-slate-500 font-mono">{s.partita_iva && `P.IVA ${s.partita_iva}`}</div></td>
                <td className="px-4 py-3">{s.referente || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.login_email}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="inline-flex gap-1 flex-wrap">
                    <Perm on={s.permessi?.view} l="Visualizza" /><Perm on={s.permessi?.download} l="Scarica" /><Perm on={s.permessi?.upload} l="Carica" />
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => setAssign(s)} data-testid={`assign-${s.id}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Building2 size={14} /> {s.companies_count} aziende</button>
                </td>
                <td className="px-4 py-3">
                  <select value={s.stato} onChange={(e) => setStato(s, e.target.value)} data-testid={`stato-${s.id}`} className={`px-2 py-1 rounded-full text-xs font-semibold border-0 ${STATO[s.stato]}`}>
                    <option value="attivo">ATTIVO</option><option value="sospeso">SOSPESO</option><option value="disattivato">DISATTIVATO</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => resetCreds(s)} className="text-slate-700 p-1.5" title="Reset credenziali" data-testid={`reset-${s.id}`}><KeyRound size={16} /></button>
                  <button onClick={() => setEdit({ ...s, password: "" })} className="text-slate-700 p-1.5" title="Modifica"><Pencil size={16} /></button>
                  <button onClick={() => remove(s)} className="text-red-600 p-1.5" title="Elimina"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Nessuno Studio di Consulenza. Crea il primo account.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <StudioDialog studio={edit} onClose={() => setEdit(null)} onSaved={(c) => { load(); if (c) setCreds(c); }} />}
      {assign && <AssignDialog studio={assign} onClose={() => setAssign(null)} onSaved={load} />}
      {creds && <CredsDialog creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

const Perm = ({ on, l }) => <span className={`px-2 py-0.5 rounded-full ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400 line-through"}`}>{l}</span>;

function Shell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-lg border border-slate-200 shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200"><h3 className="font-display font-semibold text-lg">{title}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function StudioDialog({ studio, onClose, onSaved }) {
  const [f, setF] = useState(studio);
  const [busy, setBusy] = useState(false);
  const isNew = !studio.id;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/studios", f);
        toast.success("Studio creato"); onSaved({ login_email: data.login_email, password: data.initial_password, denominazione: data.denominazione });
      } else { await api.put(`/studios/${studio.id}`, f); toast.success("Studio aggiornato"); onSaved(null); }
      onClose();
    } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Errore"); }
    finally { setBusy(false); }
  };
  const fields = [["denominazione", "Denominazione Studio *", true], ["referente", "Referente (nome e cognome)"], ["codice_fiscale", "Codice fiscale"], ["partita_iva", "Partita IVA"], ["indirizzo", "Indirizzo"], ["telefono", "Telefono"], ["email", "E-mail"], ["pec", "PEC"]];
  return (
    <Shell title={isNew ? "Crea Studio di Consulenza" : `Modifica ${studio.denominazione}`} onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map(([k, l, req]) => (
          <div key={k} className={k === "denominazione" || k === "indirizzo" ? "md:col-span-2" : ""}>
            <label className="text-sm font-medium text-slate-700">{l}</label>
            <input required={req} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} data-testid={`studio-${k}`} />
          </div>
        ))}
        <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1 text-xs font-mono uppercase tracking-widest text-blue-700 flex items-center gap-1"><ShieldCheck size={14} /> Accesso e permessi</div>
        <div><label className="text-sm font-medium text-slate-700">Username / e-mail di accesso *</label>
          <input required type="email" value={f.login_email || ""} onChange={(e) => setF({ ...f, login_email: e.target.value })} className={inp} data-testid="studio-login_email" /></div>
        {isNew && <div><label className="text-sm font-medium text-slate-700">Password iniziale <span className="text-slate-400 font-normal">(vuota = generata)</span></label>
          <input value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} className={`${inp} font-mono`} data-testid="studio-password" /></div>}
        <div><label className="text-sm font-medium text-slate-700">Stato account</label>
          <select value={f.stato} onChange={(e) => setF({ ...f, stato: e.target.value })} className={inp} data-testid="studio-stato">
            <option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="disattivato">Disattivato</option></select></div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-700 block mb-1">Permessi sulle aziende assegnate</label>
          <div className="flex flex-wrap gap-2">
            {[["view", "Visualizza / consulta"], ["download", "Scarica documenti"], ["upload", "Carica / modifica documenti"]].map(([k, l]) => (
              <label key={k} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${f.permessi?.[k] ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700"}`}>
                <input type="checkbox" className="hidden" checked={!!f.permessi?.[k]} disabled={k === "view"} data-testid={`studio-perm-${k}`}
                  onChange={(e) => setF({ ...f, permessi: { ...f.permessi, [k]: e.target.checked } })} />{l}
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" disabled={busy} data-testid="studio-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60">{isNew ? "Crea Studio" : "Salva"}</button>
        </div>
      </form>
    </Shell>
  );
}

function AssignDialog({ studio, onClose, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  useEffect(() => {
    Promise.all([api.get("/companies"), api.get(`/studios/${studio.id}`)]).then(([c, s]) => {
      setCompanies(c.data); setSel(new Set(s.data.companies.map(x => x.id)));
    });
  }, [studio.id]);
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  const save = async () => {
    await api.put(`/studios/${studio.id}/companies`, { company_ids: [...sel] });
    toast.success(`${sel.size} aziende assegnate a ${studio.denominazione}`); onSaved(); onClose();
  };
  const list = companies.filter(c => !q || c.ragione_sociale.toLowerCase().includes(q.toLowerCase()));
  return (
    <Shell title={`Aziende visibili a: ${studio.denominazione}`} onClose={onClose} wide>
      <input placeholder="Cerca azienda…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-3" />
      <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {list.map(c => (
          <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
            <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} data-testid={`assign-company-${c.id}`} />
            <span className="flex-1">{c.ragione_sociale}</span>
            {c.studio_id && c.studio_id !== studio.id && <span className="text-xs text-amber-700">già assegnata a {c.studio_name}</span>}
          </label>
        ))}
      </div>
      <div className="flex justify-between items-center pt-4">
        <span className="text-sm text-slate-500">{sel.size} selezionate</span>
        <button onClick={save} data-testid="assign-save" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Salva assegnazione</button>
      </div>
    </Shell>
  );
}

function CredsDialog({ creds, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(`${creds.login_email} / ${creds.password}`); toast.success("Copiato"); };
  return (
    <Shell title="Credenziali di accesso" onClose={onClose}>
      <p className="text-sm text-slate-600 mb-3">Credenziali per <b>{creds.denominazione}</b>. Sono state inviate via email e vengono mostrate una sola volta.</p>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-sm space-y-1" data-testid="studio-creds">
        <div><span className="text-slate-500">Login:</span> {creds.login_email}</div>
        <div><span className="text-slate-500">Password:</span> {creds.password}</div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button onClick={copy} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"><Copy size={14} /> Copia</button>
        <button onClick={onClose} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm">Chiudi</button>
      </div>
    </Shell>
  );
}
